/**
 * MessengerFAB — 149-cb.
 *
 * Fixed floating button di kanan-bawah aplikasi. Menampilkan badge unread total,
 * membuka MessengerDrawer. Hanya render kalau user punya perm messages.active+view.
 * Polling `/messaging/unread-count` setiap 20s + realtime via NotificationConnectionManager.
 */
import { useEffect, useRef, useState } from "react";
import { MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import useMessagingPrefs from "@/hooks/useMessagingPrefs";
import MessengerDrawer from "./MessengerDrawer";
import MessageToastLayer from "./MessageToastLayer";

export default function MessengerFAB() {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [status, setStatus] = useState("polling"); // "realtime" | "polling" | "reconnecting"
  // 16-CHAT-UX-NOTIFICATION-R2: expose conversation-id yang sedang dibuka supaya
  // MessageToastLayer bisa suppress popup untuk conv aktif. Juga initialConversationId
  // di-set saat user klik "Balas" dari toast → auto-open drawer & pilih conv.
  const [openConvId, setOpenConvId] = useState(null);
  const [initialConvId, setInitialConvId] = useState(null);
  const pollRef = useRef(null);
  const wsRef = useRef(null);

  const isSuper = user?.division === "BOD" && Number(user?.level) === 1;
  const canMsg = isSuper || (canPerm(user, "messages", "active") && canPerm(user, "messages", "view"));
  const { prefs } = useMessagingPrefs(!!canMsg);

  const refreshUnread = async () => {
    try {
      const { data } = await api.get("/messaging/unread-count");
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  };

  // 149-ck: Heartbeat + polling — pause saat tab tidak aktif (menghemat quota
  // API + tidak spam server ketika user tidak melihat aplikasi). Resume
  // otomatis saat tab kembali visible + refresh 1x agar badge fresh.
  useEffect(() => {
    if (!canMsg) return;
    let stopped = false;
    let backoff = 20000; // ms — akan naik s/d 60s kalau gagal berturut
    const tick = async () => {
      if (stopped || document.hidden) return;
      try {
        await refreshUnread();
        backoff = 20000; // reset saat sukses
        setStatus((s) => s === "realtime" ? s : "polling");
      } catch {
        backoff = Math.min(60000, backoff * 1.5);
        setStatus("reconnecting");
      } finally {
        if (!stopped) pollRef.current = setTimeout(tick, backoff);
      }
    };
    const hb = setInterval(() => {
      if (document.hidden) return;
      api.post("/messaging/presence/heartbeat").catch(() => {});
    }, 45000);
    api.post("/messaging/presence/heartbeat").catch(() => {});
    // First tick
    refreshUnread();
    pollRef.current = setTimeout(tick, backoff);
    // Resume on visibility change
    const onVis = () => {
      if (!document.hidden) {
        refreshUnread();
        // reset timer supaya langsung tick lagi setelah beberapa detik
        if (pollRef.current) clearTimeout(pollRef.current);
        pollRef.current = setTimeout(tick, 3000);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      if (pollRef.current) clearTimeout(pollRef.current);
      clearInterval(hb);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [canMsg]);

  // Listen to WS events dispatched by AppShell's NotificationConnectionManager
  useEffect(() => {
    const onWs = (e) => {
      const p = e.detail || {};
      if (["message_new", "message_edit", "message_read"].includes(p.type)) {
        setStatus("realtime");
        refreshUnread();
        // dispatch to drawer if open
        window.dispatchEvent(new CustomEvent("ens:msg", { detail: p }));
      }
    };
    window.addEventListener("ens:ws", onWs);
    return () => window.removeEventListener("ens:ws", onWs);
  }, []);

  // Logout / unload → mark offline best-effort
  useEffect(() => {
    if (!canMsg) return;
    const off = () => { try { navigator.sendBeacon?.(`${api.defaults.baseURL}/messaging/presence/offline`); } catch {} };
    window.addEventListener("beforeunload", off);
    return () => window.removeEventListener("beforeunload", off);
  }, [canMsg]);

  if (!canMsg || !user) return null;
  // Hormati preferensi notifikasi pesan: kalau user matikan notif, tombol tetap
  // ada (agar user tetap bisa buka drawer manual) tapi badge disembunyikan.
  const showBadge = prefs?.notif !== false;
  return (
    <>
      <button
        type="button"
        data-testid="messenger-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Buka Pesan Internal${showBadge && unread > 0 ? ` — ${unread} belum dibaca` : ""}`}
        title={`Pesan Internal${showBadge && unread > 0 ? ` · ${unread} baru` : ""}${prefs?.dnd ? " · DND aktif" : ""}`}
        className="fixed bottom-5 right-5 z-40 w-12 h-12 rounded-full bg-[#0B1D3A] text-white shadow-2xl hover:bg-[#1a2e52] flex items-center justify-center transition-transform hover:scale-105"
      >
        <MessageSquare size={20} />
        {showBadge && unread > 0 && (
          <span data-testid="messenger-fab-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-[#FF5A1F] rounded-full text-[10px] font-bold flex items-center justify-center tabular-nums border-2 border-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
        {prefs?.dnd && (
          <span aria-hidden="true" title="DND aktif" className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-amber-500 rounded-full border-2 border-white" />
        )}
      </button>
      {open && (
        <MessengerDrawer
          onClose={() => { setOpen(false); setOpenConvId(null); setInitialConvId(null); }}
          onUnreadChange={setUnread}
          realtimeStatus={status}
          setRealtimeStatus={setStatus}
          initialConversationId={initialConvId}
          onSelectedConvChange={setOpenConvId}
        />
      )}
      {/* 16-CHAT-UX-NOTIFICATION-R2: layer popup notifikasi (kanan bawah, di atas FAB).
          Tidak dirender saat drawer sedang buka & conv-id sama — dihandle di dalam
          MessageToastLayer via prop drawerOpen + currentConversationId. */}
      <MessageToastLayer
        drawerOpen={open}
        currentConversationId={openConvId}
        onReply={(cid) => { setInitialConvId(cid); setOpen(true); }}
      />
    </>
  );
}
